import { useEffect, useMemo, useState } from "react";
import intl from "react-intl-universal";
import { Link } from "react-router-dom";
import { TbArrowLeft, TbCopy, TbUnlink } from "react-icons/tb";
import { useBaasInstance } from "../../../../Contexts/BaasInstanceContext";
import { useBaasPlatformCreateAccess } from "../../../../Hooks/useBaasPlatformCreateAccess";
import { useBaasPageLoading } from "../../../../Hooks/useBaasPageLoading";
import { useToast } from "../../../../Hooks/toast";
import {
  fetchIssuerFunding,
  putIssuerFunding,
} from "../../../../Services/baasIssuerFundingApi";
import { formatBaasConsoleConflictMessage } from "../../../../Services/baasConsoleClient";
import { isValutMatrixConsolePin } from "../../../../Services/baasEntidadesApi";
import { BaasCardProgramDetailSkeleton } from "../components/BaasSkeleton";
import { BAAS_CARTOES_PATH } from "../constants";

const formatDocument = (digits) => {
  const raw = String(digits || "").replace(/\D/g, "");
  if (raw.length === 11) {
    return raw.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4");
  }
  if (raw.length === 14) {
    return raw.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, "$1.$2.$3/$4-$5");
  }
  return digits || "—";
};

const inputClass =
  "h-10 w-full rounded-lg border border-gray-200 px-3 text-sm text-brand-dark outline-none transition focus:border-[#e7a73f] dark:border-brand-dark-border dark:bg-brand-dark-elevated dark:text-gray-100";

const toggleTrackClass = (on) =>
  `relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition ${
    on ? "bg-[#e7a73f]" : "bg-gray-200 dark:bg-gray-700"
  }`;

const toggleThumbClass = (on) =>
  `pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow transition ${
    on ? "translate-x-5" : "translate-x-0"
  }`;

export const BaasCartoesIssuerSettingsPage = () => {
  const { selectedInstance } = useBaasInstance();
  const { addToast } = useToast();
  const { isValutConfigAdmin } = useBaasPlatformCreateAccess();
  const canWrite = Boolean(isValutConfigAdmin);
  const isLoading = useBaasPageLoading(selectedInstance?.id);
  const [funding, setFunding] = useState(null);
  const [dataLoading, setDataLoading] = useState(false);
  const [originDraft, setOriginDraft] = useState("");
  const [selectedKey, setSelectedKey] = useState("");
  const [saving, setSaving] = useState(false);
  const [maxProgramsDraft, setMaxProgramsDraft] = useState(10);
  const [issuanceDraft, setIssuanceDraft] = useState("both");

  useEffect(() => {
    let cancelled = false;

    (async () => {
      if (!selectedInstance) {
        setFunding(null);
        return;
      }

      setDataLoading(true);
      try {
        const next = await fetchIssuerFunding(selectedInstance);
        if (!cancelled) {
          setFunding(next);
          setOriginDraft(next?.lockedOriginDocument || "");
          setMaxProgramsDraft(next?.maxCardPrograms ?? 10);
          setIssuanceDraft(next?.cardIssuanceEntityType || "both");
          setSelectedKey("");
        }
      } catch (error) {
        if (!cancelled) {
          setFunding(null);
          addToast({
            type: "error",
            title: formatBaasConsoleConflictMessage(
              error,
              intl.get("baas.cartoes.issuerSettings.loadError"),
            ),
          });
        }
      } finally {
        if (!cancelled) setDataLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [addToast, selectedInstance]);

  const keyOptions = useMemo(() => {
    const bound = funding?.pixKey;
    const available = funding?.availableKeys || [];
    if (!bound) return available;
    return [bound, ...available.filter((key) => key.key !== bound.key)];
  }, [funding]);

  const boundKey = funding?.pixKey;
  const fundingEnabled = Boolean(funding?.enabled);
  const alreadyLinked = Boolean(boundKey?.key);
  const isMatrixConsole = isValutMatrixConsolePin(selectedInstance);
  const isLocallyBound =
    alreadyLinked &&
    Boolean(boundKey?.linkedAccountId) &&
    boundKey.linkedAccountId === funding?.programReserveAccountId;
  const showBoundKey = alreadyLinked && (fundingEnabled || canWrite);
  const showBindForm =
    canWrite &&
    fundingEnabled &&
    !alreadyLinked &&
    keyOptions.some((key) => !key.linked);
  const showUnbind = canWrite && alreadyLinked && (isLocallyBound || isMatrixConsole);
  const showOriginEditor = canWrite && !alreadyLinked;
  const showOriginDisplay = Boolean(funding?.lockedOriginDocument) && (fundingEnabled || canWrite);

  const handleCopy = async (value) => {
    try {
      await navigator.clipboard.writeText(value);
      addToast({
        type: "success",
        title: intl.get("baas.cartoes.issuerSettings.copied"),
      });
    } catch {
      addToast({
        type: "error",
        title: intl.get("baas.cartoes.issuerSettings.copyError"),
      });
    }
  };

  const handleToggle = async (nextEnabled) => {
    if (!selectedInstance || !canWrite) return;
    setSaving(true);
    try {
      const next = await putIssuerFunding(selectedInstance, { enabled: nextEnabled });
      setFunding(next);
      setOriginDraft(next?.lockedOriginDocument || "");
      setMaxProgramsDraft(next?.maxCardPrograms ?? 10);
      setIssuanceDraft(next?.cardIssuanceEntityType || "both");
      setSelectedKey("");
      addToast({
        type: "success",
        title: intl.get("baas.cartoes.issuerSettings.toggleSuccess"),
      });
    } catch (error) {
      addToast({
        type: "error",
        title: formatBaasConsoleConflictMessage(
          error,
          intl.get("baas.cartoes.issuerSettings.saveError"),
        ),
      });
    } finally {
      setSaving(false);
    }
  };

  const handleSaveProgramLimits = async () => {
    if (!selectedInstance || !canWrite) return;
    const max = Number(maxProgramsDraft);
    if (!Number.isFinite(max) || max < 1 || max > 1000) {
      addToast({
        type: "error",
        title: intl.get("baas.cartoes.issuerSettings.maxProgramsInvalid"),
      });
      return;
    }
    setSaving(true);
    try {
      const next = await putIssuerFunding(selectedInstance, {
        max_card_programs: Math.trunc(max),
        card_issuance_entity_type: issuanceDraft,
      });
      setFunding(next);
      setMaxProgramsDraft(next?.maxCardPrograms ?? max);
      setIssuanceDraft(next?.cardIssuanceEntityType || issuanceDraft);
      addToast({
        type: "success",
        title: intl.get("baas.cartoes.issuerSettings.programLimitsSaveSuccess"),
      });
    } catch (error) {
      addToast({
        type: "error",
        title: formatBaasConsoleConflictMessage(
          error,
          intl.get("baas.cartoes.issuerSettings.saveError"),
        ),
      });
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async () => {
    if (!selectedInstance || !canWrite) return;
    const option = keyOptions.find(
      (key) => `${key.originAccountId}::${key.keyId || key.key}` === selectedKey,
    );
    setSaving(true);
    try {
      const body = {
        enabled: fundingEnabled,
        locked_origin_document: originDraft.trim() || undefined,
      };
      if (option && !option.linked) {
        body.origin_account_id = option.originAccountId;
        body.pix_key = option.keyId || option.key;
      }
      const next = await putIssuerFunding(selectedInstance, body);
      setFunding(next);
      setOriginDraft(next?.lockedOriginDocument || "");
      setMaxProgramsDraft(next?.maxCardPrograms ?? maxProgramsDraft);
      setIssuanceDraft(next?.cardIssuanceEntityType || issuanceDraft);
      setSelectedKey("");
      addToast({
        type: "success",
        title: intl.get("baas.cartoes.issuerSettings.saveSuccess"),
      });
    } catch (error) {
      addToast({
        type: "error",
        title: formatBaasConsoleConflictMessage(
          error,
          intl.get("baas.cartoes.issuerSettings.saveError"),
        ),
      });
    } finally {
      setSaving(false);
    }
  };

  const handleUnbind = async () => {
    if (!selectedInstance || !canWrite || !boundKey) return;
    setSaving(true);
    try {
      const next = await putIssuerFunding(selectedInstance, { unbind: true });
      setFunding(next);
      setOriginDraft(next?.lockedOriginDocument || "");
      setSelectedKey("");
      addToast({
        type: "success",
        title: intl.get("baas.cartoes.issuerSettings.unbindSuccess"),
      });
    } catch (error) {
      addToast({
        type: "error",
        title: formatBaasConsoleConflictMessage(
          error,
          intl.get("baas.cartoes.issuerSettings.saveError"),
        ),
      });
    } finally {
      setSaving(false);
    }
  };

  if (isLoading || dataLoading) {
    return <BaasCardProgramDetailSkeleton />;
  }

  return (
    <div className="baas-processing min-w-0 space-y-5">
      <header className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <p className="text-xs font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500">
            {intl.get("baas.nav.cartoes")}
          </p>
          <h1 className="mt-1 text-2xl font-bold text-brand-dark dark:text-gray-100">
            {intl.get("baas.cartoes.issuerSettings.title")}
          </h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            {intl.get("baas.cartoes.issuerSettings.subtitle")}
          </p>
        </div>
        <Link to={BAAS_CARTOES_PATH} className="btn-outline w-auto shrink-0 self-start !text-xs">
          <TbArrowLeft className="h-4 w-4" />
          {intl.get("baas.cartoes.programs.detail.back")}
        </Link>
      </header>

      <section className="space-y-4 rounded-xl border border-gray-200 bg-white p-4 dark:border-brand-dark-border dark:bg-brand-dark-surface">
        <div>
          <p className="dashboard-section-label">
            {intl.get("baas.cartoes.issuerSettings.programLimitsTitle")}
          </p>
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            {canWrite
              ? intl.get("baas.cartoes.issuerSettings.programLimitsHint")
              : intl.get("baas.cartoes.issuerSettings.operatorReadOnly")}
          </p>
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <label className="block text-sm">
            <span className="mb-1 block text-gray-600 dark:text-gray-400">
              {intl.get("baas.cartoes.issuerSettings.maxPrograms")}
            </span>
            {canWrite ? (
              <input
                type="number"
                min={1}
                max={1000}
                className={inputClass}
                value={maxProgramsDraft}
                disabled={saving}
                onChange={(event) => setMaxProgramsDraft(event.target.value)}
              />
            ) : (
              <p className="text-sm text-brand-dark dark:text-gray-100">
                {funding?.maxCardPrograms ?? "—"}
              </p>
            )}
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              {intl.get("baas.cartoes.issuerSettings.maxProgramsCount", {
                count: funding?.cardProgramCount ?? 0,
                max: funding?.maxCardPrograms ?? maxProgramsDraft,
              })}
            </p>
          </label>
          <fieldset className="block text-sm">
            <legend className="mb-1 block text-gray-600 dark:text-gray-400">
              {intl.get("baas.cartoes.issuerSettings.issuanceEntityType")}
            </legend>
            <div className="flex flex-wrap gap-2">
              {[
                { value: "pf", labelKey: "baas.cartoes.issuerSettings.issuancePf" },
                { value: "pj", labelKey: "baas.cartoes.issuerSettings.issuancePj" },
                { value: "both", labelKey: "baas.cartoes.issuerSettings.issuanceBoth" },
              ].map((option) => {
                const selected = issuanceDraft === option.value;
                return (
                  <button
                    key={option.value}
                    type="button"
                    disabled={!canWrite || saving}
                    className={`rounded-lg border px-3 py-2 text-xs transition ${
                      selected
                        ? "border-[#e7a73f] bg-[#e7a73f]/10 text-brand-dark dark:text-gray-100"
                        : "border-gray-200 text-gray-600 dark:border-brand-dark-border dark:text-gray-400"
                    }`}
                    onClick={() => canWrite && setIssuanceDraft(option.value)}
                  >
                    {intl.get(option.labelKey)}
                  </button>
                );
              })}
            </div>
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              {intl.get("baas.cartoes.issuerSettings.issuanceEntityTypeHint")}
            </p>
          </fieldset>
        </div>
        {canWrite ? (
          <button
            type="button"
            className="btn-primary w-auto !text-xs"
            disabled={saving}
            onClick={handleSaveProgramLimits}
          >
            {intl.get("baas.cartoes.issuerSettings.saveProgramLimits")}
          </button>
        ) : null}
      </section>

      <div className="flex w-full items-center justify-between gap-3 rounded-xl border border-gray-200 bg-white px-4 py-3 dark:border-brand-dark-border dark:bg-brand-dark-surface">
        <div className="min-w-0">
          <p className="text-sm font-medium text-brand-dark dark:text-gray-100">
            {intl.get("baas.cartoes.issuerSettings.toggleLabel")}
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {canWrite
              ? intl.get("baas.cartoes.issuerSettings.toggleHint")
              : intl.get("baas.cartoes.issuerSettings.operatorReadOnly")}
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
            {fundingEnabled
              ? intl.get("baas.cartoes.issuerSettings.toggleOn")
              : intl.get("baas.cartoes.issuerSettings.toggleOff")}
          </span>
          <button
            type="button"
            role="switch"
            aria-checked={fundingEnabled}
            className={`${toggleTrackClass(fundingEnabled)} ${
              !canWrite || saving ? "cursor-not-allowed opacity-60" : ""
            }`}
            onClick={() => canWrite && !saving && handleToggle(!fundingEnabled)}
            disabled={!canWrite || saving}
          >
            <span className={toggleThumbClass(fundingEnabled)} />
          </button>
        </div>
      </div>

      {!fundingEnabled ? (
        <div className="rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-600 dark:border-brand-dark-border dark:bg-brand-dark-elevated dark:text-gray-400">
          {intl.get("baas.cartoes.issuerSettings.disabledHint")}
        </div>
      ) : null}

      {funding?.needsManualAllocation ? (
        <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:border-amber-900/40 dark:bg-amber-950/30 dark:text-amber-200">
          {funding.manualAllocationNote
            || intl.get("baas.cartoes.issuerSettings.manualAllocation")}
        </div>
      ) : null}

      {fundingEnabled || canWrite ? (
      <section className="space-y-4 rounded-xl border border-gray-200 bg-white p-4 dark:border-brand-dark-border dark:bg-brand-dark-surface">
        <div>
          <p className="dashboard-section-label">
            {intl.get("baas.cartoes.issuerSettings.pixKeySection")}
          </p>
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            {intl.get("baas.cartoes.issuerSettings.pixKeyHint")}
          </p>
        </div>

        {showBoundKey ? (
          <div className="rounded-lg border border-gray-100 bg-gray-50 px-3 py-3 dark:border-brand-dark-border dark:bg-brand-dark-elevated">
            <p className="text-xs uppercase tracking-wide text-gray-400">
              {boundKey.keyType || "PIX"}
            </p>
            <p className="mt-1 break-all font-mono text-sm text-brand-dark dark:text-gray-100">
              {boundKey.key}
            </p>
            {boundKey.originAccountLabel ? (
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                {boundKey.originAccountLabel}
              </p>
            ) : null}
            <div className="mt-3 flex flex-wrap gap-2">
              {fundingEnabled ? (
                <button
                  type="button"
                  className="btn-outline inline-flex items-center gap-1.5 !text-xs"
                  onClick={() => handleCopy(boundKey.key)}
                >
                  <TbCopy className="h-3.5 w-3.5" />
                  {intl.get("baas.cartoes.issuerSettings.copyKey")}
                </button>
              ) : null}
              {showUnbind ? (
                <button
                  type="button"
                  className="btn-outline inline-flex items-center gap-1.5 !text-xs"
                  disabled={saving}
                  onClick={handleUnbind}
                >
                  <TbUnlink className="h-3.5 w-3.5" />
                  {intl.get("baas.cartoes.issuerSettings.unbind")}
                </button>
              ) : null}
            </div>
          </div>
        ) : (
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {intl.get("baas.cartoes.issuerSettings.noKey")}
          </p>
        )}

        {showBindForm ? (
          <label className="block text-sm">
            <span className="mb-1 block text-gray-600 dark:text-gray-400">
              {intl.get("baas.cartoes.issuerSettings.bindKey")}
            </span>
            <select
              className={inputClass}
              value={selectedKey}
              onChange={(event) => setSelectedKey(event.target.value)}
            >
              <option value="">
                {intl.get("baas.cartoes.issuerSettings.bindKeyPlaceholder")}
              </option>
              {keyOptions
                .filter((key) => !key.linked)
                .map((key) => {
                  const value = `${key.originAccountId}::${key.keyId || key.key}`;
                  return (
                    <option key={value} value={value}>
                      {(key.keyType || "PIX").toUpperCase()} · {key.key}
                      {key.originAccountLabel ? ` · ${key.originAccountLabel}` : ""}
                    </option>
                  );
                })}
            </select>
          </label>
        ) : null}

        {showOriginEditor || showOriginDisplay ? (
          <label className="block text-sm">
            <span className="mb-1 block text-gray-600 dark:text-gray-400">
              {intl.get("baas.cartoes.issuerSettings.lockedOrigin")}
            </span>
            {showOriginEditor ? (
              <input
                className={inputClass}
                value={originDraft}
                disabled={!funding?.lockedOriginEditable}
                onChange={(event) => setOriginDraft(event.target.value)}
                placeholder={intl.get("baas.cartoes.issuerSettings.lockedOriginPlaceholder")}
              />
            ) : (
              <p className="font-mono text-sm text-brand-dark dark:text-gray-100">
                {formatDocument(funding?.lockedOriginDocument)}
              </p>
            )}
            {showOriginEditor ? (
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                {funding?.lockedOriginEditable
                  ? intl.get("baas.cartoes.issuerSettings.lockedOriginHint")
                  : intl.get("baas.cartoes.issuerSettings.lockedOriginLocked")}
              </p>
            ) : null}
          </label>
        ) : null}

        {showBindForm ? (
          <button
            type="button"
            className="btn-primary w-auto !text-xs"
            disabled={saving}
            onClick={handleSave}
          >
            {intl.get("baas.cartoes.issuerSettings.save")}
          </button>
        ) : null}
      </section>
      ) : null}
    </div>
  );
};
